"use client";

import Link from "next/link";

import { Card } from "@heroui/card";

import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";
import { routes } from "@/lib/routes";
import { OrganizationTerminology } from "@/lib/services/terminologyService";

interface Area {
  id: string;
  name: string;
  slug: string;
  state: string;
}

interface AreaDiscoveryProps {
  areas: Area[];
  orgSlug: string;
  className?: string;
  terminology: OrganizationTerminology;
}

export function AreaDiscovery({
  areas,
  className = "",
  orgSlug,
  terminology,
}: AreaDiscoveryProps) {
  // Don't show if only one area or no areas
  if (areas.length <= 1) {
    return null;
  }

  return (
    <Card
      className={`p-4 mb-6 bg-gradient-to-r from-primary-50 to-secondary-50 border-primary-200 ${className}`}
    >
      <div className="text-center">
        <h3 className="text-lg font-semibold text-primary-800 mb-2">
          🗺️ {terminology.termBrowse} by Specific {terminology.area.singular}
        </h3>
        <p className="text-sm text-default-600 mb-4">
          Focus on {terminology.need.plural.toLowerCase()} in your neighborhood
          or {terminology.area.singular.toLowerCase()} of interest
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          {areas.map((area) => (
            <Link
              key={area.id}
              className="text-xs inline-flex items-center gap-2 px-2 py-2 bg-white border-2 border-primary-200 text-primary-700 rounded-lg hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
              href={routes.org.area(orgSlug, area.slug)}
              onClick={() => {
                void EventPublisher.publishEvent(
                  EventKeys.AREA_DISCOVERY_LINK_CLICK,
                  {
                    areaId: area.id,
                    areaName: area.name,
                    org: orgSlug,
                  },
                );
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
                <path
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              {area.name}
            </Link>
          ))}
        </div>
      </div>
    </Card>
  );
}
